import { useState } from 'react';
import { X, Plus, Pencil, Trash2, Wallet } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useCurrency } from '@/contexts/CurrencyContext';
import { CATEGORY_ICONS } from '@/hooks/useCategories';
import type { RecurringTransaction } from '@/hooks/useRecurringTransactions';

interface ManageRecurrencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  recurrences: RecurringTransaction[];
  onAdd: () => void;
  onEdit: (recurring: RecurringTransaction) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
}

type TabFilter = 'expense' | 'income';

const FREQUENCY_MAP: Record<string, string> = {
  daily: 'calendar.manage.daily',
  weekly: 'calendar.manage.weekly',
  monthly: 'calendar.manage.monthly',
  yearly: 'calendar.manage.yearly',
};

export function ManageRecurrencesModal({
  isOpen,
  onClose,
  recurrences,
  onAdd,
  onEdit,
  onDelete,
  onToggleActive,
}: ManageRecurrencesModalProps) {
  const { t, language } = useTranslation();
  const { formatAmount } = useCurrency();
  const [activeTab, setActiveTab] = useState<TabFilter>('expense');

  if (!isOpen) return null;

  const expenses = recurrences.filter((r) => r.type === 'expense');
  const incomes = recurrences.filter((r) => r.type === 'income');
  const filtered = activeTab === 'expense' ? expenses : incomes;
  const locale = language === 'fr' ? 'fr-FR' : 'en-US';

  const formatDate = (dateStr: string) => {
    // Handle both "YYYY-MM-DD" and full timestamp formats
    const raw = dateStr.substring(0, 10);
    const [y, m, d] = raw.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('calendar.manageRecurrences' as any)}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={onAdd}
              className="flex items-center gap-2 px-4 py-2 bg-[#16c47f] hover:bg-[#14b371] text-white rounded-xl text-sm font-medium transition-all"
            >
              <Plus className="w-4 h-4" />
              {t('calendar.add' as any)}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-50 dark:border-gray-700/50">
          <button
            onClick={() => setActiveTab('expense')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'expense'
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            {t('calendar.expenses' as any)}
            <span className="bg-[#78909C] text-white text-[10px] font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
              {expenses.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('income')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'income'
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            {t('calendar.income' as any)}
            <span className="bg-[#16c47f] text-white text-[10px] font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
              {incomes.length}
            </span>
          </button>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1 px-6 py-2">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
              <p className="text-sm">{t('calendar.manage.noRecurrences' as any)}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  <th className="py-3 pr-4">{t('calendar.manage.description' as any)}</th>
                  <th className="py-3 pr-4">{t('calendar.manage.amount' as any)}</th>
                  <th className="py-3 pr-4">{t('calendar.manage.frequency' as any)}</th>
                  <th className="py-3 pr-4">{t('calendar.manage.recurrenceDate' as any)}</th>
                  <th className="py-3 pr-4">{t('calendar.manage.endDate' as any)}</th>
                  <th className="py-3 pr-4">{t('calendar.manage.category' as any)}</th>
                  <th className="py-3 pr-4">{t('calendar.manage.account' as any)}</th>
                  <th className="py-3 pr-4">{t('calendar.manage.status' as any)}</th>
                  <th className="py-3">{t('calendar.manage.actions' as any)}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {filtered.map((r) => (
                  <tr key={r.id} className="group hover:bg-gray-50/50 dark:hover:bg-gray-700/20">
                    {/* Description */}
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        {r.category?.icon && (
                          <span className="text-base">
                            {CATEGORY_ICONS[r.category.icon] || r.category.icon}
                          </span>
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {r.description}
                        </span>
                      </div>
                    </td>
                    {/* Amount */}
                    <td className="py-3 pr-4">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {formatAmount(r.amount)}
                      </span>
                    </td>
                    {/* Frequency */}
                    <td className="py-3 pr-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {t(FREQUENCY_MAP[r.frequency] as any) || r.frequency}
                      </span>
                    </td>
                    {/* Recurrence date */}
                    <td className="py-3 pr-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(r.start_date)}
                      </span>
                    </td>
                    {/* End date */}
                    <td className="py-3 pr-4">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {r.end_date ? formatDate(r.end_date) : '—'}
                      </span>
                    </td>
                    {/* Category */}
                    <td className="py-3 pr-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {r.category?.name || '—'}
                      </span>
                    </td>
                    {/* Account */}
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 max-w-[120px] truncate">
                        <Wallet className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{r.account?.name || '—'}</span>
                      </div>
                    </td>
                    {/* Status toggle */}
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onToggleActive(r.id, !r.is_active)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            r.is_active ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                              r.is_active ? 'translate-x-[18px]' : 'translate-x-[3px]'
                            }`}
                          />
                        </button>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            r.is_active
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {r.is_active
                            ? t('calendar.manage.active' as any)
                            : t('calendar.manage.inactive' as any)}
                        </span>
                      </div>
                    </td>
                    {/* Actions */}
                    <td className="py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onEdit(r)}
                          className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                          <Pencil className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        </button>
                        <button
                          onClick={() => onDelete(r.id)}
                          className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
